import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listEnquiries,
  getEnquiryDetail,
  deleteEnquiry,
  updateEnquiryStatus,
  addEnquiryReply,
  editEnquiryCorrespondence,
  deleteEnquiryCorrespondence,
} from "./callbacks";
import { QData } from "../../../types/basic-type-list";

export const useEnquiryList = (qData?: QData) => {
  const queryResult = useQuery({
    queryKey: ["/enquiry", qData],
    queryFn: async () => {
      const res = await listEnquiries(qData);
      return res.data;
    },
  });

  return {
    ...queryResult,
    refetchWithQuery: (newQData: QData) => {
      queryResult.refetch();
    },
  };
};

export const useEnquiryDetail = (id?: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["/enquiry", id],
    queryFn: async () => {
      if (!id) return null;
      const res = await getEnquiryDetail(id);
      return res.data;
    },
    enabled: options?.enabled ?? !!id,
  });
};

export const useDeleteEnquiry = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEnquiry(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["/enquiry"] });
      onSuccess?.(res);
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
};

export const useUpdateEnquiryStatus = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, soldAt }: { id: string; status: string; soldAt?: string | null }) =>
      updateEnquiryStatus(id, status, soldAt),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/enquiry"] });
      queryClient.invalidateQueries({ queryKey: ["/enquiry", variables.id] });
      onSuccess?.(res);
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
};

export const useAddEnquiryReply = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reply }: { id: string; reply: string }) =>
      addEnquiryReply(id, reply),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/enquiry"] });
      queryClient.invalidateQueries({ queryKey: ["/enquiry", variables.id] });
      onSuccess?.(res);
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
};

export const useEditEnquiryCorrespondence = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      correspondenceId,
      text,
    }: {
      id: string;
      correspondenceId: string;
      text: string;
    }) => editEnquiryCorrespondence(id, correspondenceId, text),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/enquiry"] });
      queryClient.invalidateQueries({ queryKey: ["/enquiry", variables.id] });
      onSuccess?.(res);
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
};

export const useDeleteEnquiryCorrespondence = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, correspondenceId }: { id: string; correspondenceId: string }) =>
      deleteEnquiryCorrespondence(id, correspondenceId),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/enquiry"] });
      queryClient.invalidateQueries({ queryKey: ["/enquiry", variables.id] });
      onSuccess?.(res);
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
};
